import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { listSubmissions, subscribeToSubmissionList } from '../lib/db.ts';
import type { Submission } from '../lib/types.ts';
import { StatusBadge } from '../components/StatusBadge.tsx';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; rows: readonly Submission[] }
  | { kind: 'error'; message: string };

export function Jobs() {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [filter, setFilter] = useState('');

  useEffect(() => {
    let active = true;

    async function load(): Promise<void> {
      try {
        const rows = await listSubmissions();
        if (active) setState({ kind: 'ready', rows });
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        if (active) setState({ kind: 'error', message });
      }
    }

    void load();
    const channel = subscribeToSubmissionList(() => {
      void load();
    });

    return () => {
      active = false;
      void channel.unsubscribe();
    };
  }, []);

  const visible = useMemo(() => {
    if (state.kind !== 'ready') return [];
    const q = filter.trim().toLowerCase();
    if (!q) return state.rows;
    return state.rows.filter(
      (r) => r.job_name.toLowerCase().includes(q) || r.source_pdf.toLowerCase().includes(q),
    );
  }, [state, filter]);

  return (
    <div style={{ maxWidth: 960 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
        <h1 style={{ fontFamily: "'Syne', sans-serif", fontSize: 28, fontWeight: 800 }}>Jobs</h1>
        <Link
          to="/app/jobs/new"
          style={{
            background: '#00E87A',
            color: '#030804',
            fontFamily: "'Syne', sans-serif",
            fontWeight: 700,
            fontSize: 12,
            padding: '10px 18px',
            borderRadius: 6,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
          }}
        >
          + New job
        </Link>
      </div>

      <input
        type="text"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Search by job name or filename"
        style={{
          width: '100%',
          maxWidth: 480,
          background: '#0D0F1C',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 6,
          padding: '9px 12px',
          color: '#F0F2FF',
          fontFamily: "'Outfit', sans-serif",
          fontSize: 13,
          marginBottom: 18,
          outline: 'none',
        }}
      />

      {state.kind === 'loading' && <div style={{ color: '#525870', fontSize: 13 }}>Loading…</div>}
      {state.kind === 'error' && (
        <div style={{ color: '#FF6B7A', fontSize: 13, padding: 16, border: '1px solid rgba(255,107,122,0.3)', borderRadius: 8 }}>
          {state.message}
        </div>
      )}

      {state.kind === 'ready' && visible.length === 0 && (
        <div
          style={{
            border: '1px dashed rgba(255,255,255,0.12)',
            borderRadius: 10,
            padding: '36px 28px',
            textAlign: 'center',
            color: '#B8BDD6',
          }}
        >
          <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 16, fontWeight: 700, marginBottom: 8, color: '#F0F2FF' }}>
            No jobs yet
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>
            Drop a drawing set on{' '}
            <Link to="/app/jobs/new" style={{ color: '#00E87A' }}>
              New job
            </Link>{' '}
            to kick off a takeoff.
          </div>
        </div>
      )}

      {state.kind === 'ready' && visible.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: '#525870', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              <th style={{ padding: '10px 8px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Job</th>
              <th style={{ padding: '10px 8px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Status</th>
              <th style={{ padding: '10px 8px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Kind</th>
              <th style={{ padding: '10px 8px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Created</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <tr key={row.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <td style={{ padding: '12px 8px' }}>
                  <Link
                    to={`/app/jobs/${row.id}`}
                    style={{ color: '#F0F2FF', display: 'flex', flexDirection: 'column', gap: 2 }}
                  >
                    <span style={{ fontWeight: 600 }}>{row.job_name}</span>
                    <span style={{ color: '#525870', fontFamily: "'JetBrains Mono', monospace", fontSize: 10 }}>
                      {row.source_pdf}
                    </span>
                  </Link>
                </td>
                <td style={{ padding: '12px 8px' }}>
                  <StatusBadge status={row.status} />
                </td>
                <td style={{ padding: '12px 8px', color: '#B8BDD6', fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>
                  {row.kind}
                </td>
                <td style={{ padding: '12px 8px', color: '#525870', fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>
                  {formatRelative(row.created_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}
